import events from "@mongez/events";
import { Random } from "@mongez/reinforcements";
import Hls from "hls.js";
import { Ref, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  VideoPlayer2Event,
  VideoPlayer2Props,
  VideoPlayer2Translation,
  VideoPlayerManager2,
} from "../types";
import { VIDE_PLAYER_2_DEFAULT_TRANSLATION } from "../utils";

export function useHls(props: VideoPlayer2Props) {
  const hlsRef = useRef<Hls | undefined>();

  if (!props.usesHLS) return undefined;

  if (hlsRef.current) return hlsRef.current;

  hlsRef.current = new Hls({
    // debug: process.env.NODE_ENV === "development",
    autoStartLoad: true,
    // ABR configuration for auto quality
    abrEwmaFastLive: 3.0, // Faster adaptation for live streams
    abrEwmaSlowLive: 9.0, // Slower adaptation for stability
    abrEwmaDefaultEstimate: 500000, // Initial bitrate estimate (500kbps)
    xhrSetup(xhr, url) {
      if (props.validateHlsHeaders) {
        if (!props.validateHlsHeaders(url)) return;
      }

      if (props.hlsHeaders) {
        Object.entries(props.hlsHeaders).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }
    },
    fetchSetup(context, initParams) {
      if (props.validateHlsHeaders) {
        if (!props.validateHlsHeaders(context.url))
          return new Request(context.url, initParams);
      }

      if (props.hlsHeaders) {
        Object.entries(props.hlsHeaders).forEach(([key, value]) => {
          initParams.headers[key] = value;
        });
      }

      return new Request(props.url, initParams);
    },
    ...props.hlsOptions,
  });

  return hlsRef.current;
}

export function useVVideoPlayer2(
  props: VideoPlayer2Props,
  ref: Ref<VideoPlayerManager2>,
) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const hls = useHls(props);
  const [controlsVisible, setControlsVisible] = useState(true);

  const [playerManager] = useState<VideoPlayerManager2>(
    () =>
      ({
        videoRef: videoRef,
        rootRef: rootRef,
        controls: props.controls ?? true,
        hls,
        get element() {
          return videoRef.current;
        },
        id: props.id || Random.id(),
        url: props.url,
        props,
        isReady: !props.usesHLS,
        qualityLevels: [],
        currentQualityLevel: 0,
        setCurrentQualityLevel: () => {},
        isPlaying: false,
        isLoading: false,
        isBuffering: false,
        isError: false,
        isEnded: false,
        isMuted: false,
        trans: (key: keyof VideoPlayer2Translation) => {
          return (
            props.translation?.[key] || VIDE_PLAYER_2_DEFAULT_TRANSLATION[key]
          );
        },
        triggerEvent: (event: VideoPlayer2Event, data: any) => {
          events.emit(`video-player-2-${playerManager.id}-${event}`, data);
        },
        on: (event: VideoPlayer2Event, callback: (data: any) => void) => {
          return events.subscribe(
            `video-player-2-${playerManager.id}-${event}`,
            callback,
          );
        },
        $lazyGetter: (
          property: keyof VideoPlayerManager2,
          valueCallback: () => any,
        ) => {
          return Object.defineProperty(playerManager, property, {
            get: valueCallback,
            enumerable: true,
            configurable: true,
          });
        },
        controlsVisible,
        setControlsVisible,
      } as unknown as VideoPlayerManager2),
  );

  useImperativeHandle(ref, () => playerManager);

  useEffect(() => {
    if (playerManager.props.usesHLS) return;

    playerManager.element.src = props.url;
  }, [playerManager.props.usesHLS, props.url, playerManager.element]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    playerManager.$lazyGetter("bufferedTime", () => {
      if (!video.readyState || !video.buffered || video.buffered.length === 0)
        return 0;
      return video.buffered.end(video.buffered.length - 1) || 0;
    });

    const updateBuffered = () => {
      if (video.buffered.length) {
        // Get the end of the last buffered range
        playerManager.triggerEvent("bufferedTimeChanged", playerManager);
      }
    };

    video.addEventListener("progress", updateBuffered);
    // Also update on timeupdate for more accuracy
    video.addEventListener("timeupdate", updateBuffered);

    // Initial update
    updateBuffered();

    return () => {
      video.removeEventListener("progress", updateBuffered);
      video.removeEventListener("timeupdate", updateBuffered);
    };
  }, [videoRef]);

  return playerManager;
}
